import {
    Box,
    Button,
    CircularProgress,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAllPrizes } from '../../../data/api/prizes/PrizeRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';
import AddPrize from './prize/AddPrize';
import RowInfo from './prize/RowInfo';

const Prize = () => {
    const { token } = useIndex();

    const [load, setLoad] = useState(true);
    const [prizes, setPrizes] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoad(true);

        try {
            const { data } = await getAllPrizes(token);

            if (data?.success) {
                setPrizes(data?.data);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoad(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <PageWrapper>
            <Header>
                <Box className="icon">
                    <EmojiEventsOutlinedIcon />
                </Box>

                <Box>
                    <Typography className="title">
                        Tablas de premios
                    </Typography>

                    <Typography className="subtitle">
                        Administra los premios, compensaciones y reglas por cantidad de aciertos.
                    </Typography>
                </Box>
            </Header>

            <MainCard>
                <Toolbar>
                    <Box>
                        <Typography className="section-title">
                            Premios configurados
                        </Typography>

                        <Typography className="section-subtitle">
                            Total de registros: <strong>{prizes?.length ?? 0}</strong>
                        </Typography>
                    </Box>

                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpen(true)}
                        sx={{
                            height: 36,
                            px: 2,
                            borderRadius: '12px',
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                            boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                boxShadow: '0 8px 18px rgba(15, 34, 77, 0.24)',
                            },
                        }}
                    >
                        Agregar
                    </Button>
                </Toolbar>

                {load ? (
                    <LoadingBox>
                        <CircularProgress sx={{ color: '#d6aa3a' }} />
                        <Typography>Cargando premios...</Typography>
                    </LoadingBox>
                ) : (
                    <StyledTableContainer>
                        <Table stickyHeader aria-label="result table" size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledHeadCell>Aciertos</StyledHeadCell>
                                    <StyledHeadCell>Premio</StyledHeadCell>
                                    <StyledHeadCell>Compensación</StyledHeadCell>
                                    <StyledHeadCell>Modificación</StyledHeadCell>
                                    <StyledHeadCell align="center">Editar</StyledHeadCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {prizes?.length > 0 ? (
                                    prizes.map((item) => (
                                        <RowInfo
                                            key={item?.uuid ?? item?.id}
                                            item={item}
                                            token={token}
                                            loadData={loadData}
                                        />
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <EmptyState>
                                                <EmojiEventsOutlinedIcon />
                                                <Typography className="empty-title">
                                                    No hay premios
                                                </Typography>
                                                <Typography className="empty-text">
                                                    Agrega una tabla de premio para comenzar.
                                                </Typography>
                                            </EmptyState>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                )}
            </MainCard>

            <Modal open={open} onClose={handleClose}>
                <ModalCenter>
                    <AddPrize token={token} loadData={loadData} handleClose={handleClose} />
                </ModalCenter>
            </Modal>
        </PageWrapper>
    );
};

export default Prize;

const PageWrapper = styled(Box)`
    min-height: 100%;
    background: #f5f7fb;
    padding: 1.2rem;

    @media (max-width: 600px) {
        padding: 0.8rem;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1rem;

    .icon {
        width: 46px;
        height: 46px;
        border-radius: 15px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.3);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
            font-size: 1.55rem;
        }
    }

    .title {
        color: #10285f;
        font-size: 1.65rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        margin-top: 0.18rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        .title {
            font-size: 1.35rem;
        }

        .subtitle {
            font-size: 0.82rem;
        }
    }
`;

const MainCard = styled.div`
    padding: 1rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.25);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08);

    @media (max-width: 600px) {
        padding: 0.85rem;
        border-radius: 18px;
    }
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    .section-title {
        color: #10285f;
        font-size: 1.05rem;
        font-weight: 900;
    }

    .section-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.82rem;
        font-weight: 600;

        strong {
            color: #10285f;
            font-weight: 900;
        }
    }

    @media (max-width: 600px) {
        button {
            width: 100%;
        }
    }
`;

const StyledTableContainer = styled(TableContainer)`
    max-height: 500px;
    max-width: 100%;
    border-radius: 18px;
    background: #fff;
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.05);
`;

const StyledHeadCell = styled(TableCell)`
    background: #10285f !important;
    color: #f8f4e8 !important;
    font-weight: 900 !important;
    font-size: 0.82rem !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 2px solid #d6aa3a !important;
    white-space: nowrap;
`;

const LoadingBox = styled.div`
    min-height: 260px;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    align-items: center;
    justify-content: center;
    color: #10285f;
    font-weight: 700;
`;

const EmptyState = styled.div`
    min-height: 180px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    svg {
        font-size: 2.2rem;
        color: #d6aa3a;
    }

    .empty-title {
        font-weight: 900;
        font-size: 1.1rem;
    }

    .empty-text {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.88rem;
    }
`;

const ModalCenter = styled(Box)`
    min-height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    box-sizing: border-box;
    outline: none;
`;